import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  HardDrive,
  ShieldAlert,
  CheckCircle2,
  Cloud,
  Copy,
  Link as LinkIcon,
  RefreshCw,
  Unplug,
} from "lucide-react";
import { fmtDate } from "@/lib/format";

export default function NexreqDrive() {
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/nexreq-drive/status");
      setStatus(data);
      if (data.connected) {
        try {
          const b = await api.get("/nexreq-drive/backups");
          setBackups(b.data.items || []);
        } catch {}
      }
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  useEffect(() => {
    load();
    if (params.get("drive_connected")) {
      toast.success("NEXREQ Drive connected");
      params.delete("drive_connected");
      setParams(params, { replace: true });
    }
    const err = params.get("drive_error");
    if (err) {
      toast.error(`Drive connect failed: ${err}`);
      params.delete("drive_error");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try {
      setBusy(true);
      const { data } = await api.get("/nexreq-drive/authorize");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect NEXREQ Drive? Backups will stop.")) return;
    try {
      await api.post("/nexreq-drive/disconnect", {});
      toast.success("Disconnected");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const runBackup = async () => {
    try {
      setBusy(true);
      const { data } = await api.post("/nexreq-drive/backup/run", {});
      toast.success(`Backup ${data.status}`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const copyRedirect = () => {
    if (!status?.redirect_uri) return;
    navigator.clipboard.writeText(status.redirect_uri);
    toast.success("Redirect URI copied");
  };

  return (
    <div>
      <PageHeader
        title="NEXREQ Drive"
        subtitle="Google Drive backup & mapped file storage"
      />

      {status && (
        <Card className="p-5 mb-4">
          <div className="flex items-start gap-3">
            {status.connected ? (
              <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-1" />
            ) : (
              <ShieldAlert className="h-6 w-6 text-amber-600 mt-1" />
            )}
            <div className="flex-1">
              <div className="text-lg font-semibold">
                {status.connected
                  ? "NEXREQ Drive is connected"
                  : status.env_ready
                  ? "NEXREQ Drive credentials ready — click Connect"
                  : "NEXREQ Drive is not connected"}
              </div>
              <div className="text-sm text-muted-foreground">
                {status.message}
              </div>
              {status.connected && (
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  {status.authorized_by && (
                    <div>Authorized by: {status.authorized_by}</div>
                  )}
                  {status.folder_name && (
                    <div>
                      Folder: <span className="font-medium">{status.folder_name}</span>
                    </div>
                  )}
                  {status.last_backup_at && (
                    <div>
                      Last backup: {fmtDate(status.last_backup_at)} ·{" "}
                      {status.last_backup_status || "-"}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {status.connected ? (
                <>
                  <Button
                    onClick={runBackup}
                    disabled={busy}
                    data-testid="drive-run-backup"
                  >
                    <Cloud className="mr-2 h-4 w-4" /> Run backup now
                  </Button>
                  <Button
                    variant="outline"
                    onClick={load}
                    data-testid="drive-refresh"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" /> Refresh
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={disconnect}
                    data-testid="drive-disconnect"
                  >
                    <Unplug className="mr-2 h-4 w-4" /> Disconnect
                  </Button>
                </>
              ) : (
                <Button
                  onClick={connect}
                  disabled={busy || !status.env_ready}
                  data-testid="drive-connect"
                >
                  <LinkIcon className="mr-2 h-4 w-4" /> Connect Google Drive
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {status && !status.env_ready && (
        <Card className="p-5 mb-4">
          <div className="font-semibold mb-2 flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-[#255058]" />
            What we need from you
          </div>
          <div className="text-sm text-muted-foreground mb-3">
            Please create a Google Cloud OAuth 2.0 Web Application client with the
            settings below, then provide the two secrets:
          </div>
          <div className="border rounded p-3 bg-muted/40 space-y-1 text-sm">
            <div>
              <span className="font-medium">Missing env vars:</span>{" "}
              <code className="text-red-700">
                {(status.missing_credentials || []).join(", ")}
              </code>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Redirect URI:</span>
              <code className="break-all text-xs">{status.redirect_uri}</code>
              <Button
                size="sm"
                variant="ghost"
                onClick={copyRedirect}
                data-testid="drive-copy-uri"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <div>
              <span className="font-medium">Required scopes:</span>{" "}
              <code className="text-xs">
                {(status.required_scopes || []).join(", ")}
              </code>
            </div>
          </div>
          <ol className="list-decimal ml-5 mt-3 text-sm space-y-1">
            {(status.instructions_for_user || []).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        </Card>
      )}

      {status?.connected && (
        <Card className="p-5">
          <div className="font-semibold mb-3">Recent backups</div>
          {backups.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No backups yet — click "Run backup now" to create the first one.
            </div>
          ) : (
            <div className="space-y-1">
              {backups.map((b) => (
                <div
                  key={b.id}
                  className="flex justify-between border rounded px-3 py-2 text-sm"
                >
                  <div>
                    <div className="font-medium">{b.file_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtDate(b.created_at)} · by {b.by_email}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground self-center">
                    {b.size ? `${(Number(b.size) / 1024 / 1024).toFixed(2)} MB` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
